import React, { useEffect, useState } from 'react';
// Icon components removed - using inline SVGs
import { RecordingService } from '../lib/firebase/recordingService';
import { RecordingMetadata } from '../lib/firebase/recordingService';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';

interface SolutionListPanelProps {
  testId: string;
  questionId: string;
  currentRecordingId?: string;
  tenantId?: string | number;
  isMobile?: boolean;
  onMobileToggle?: () => void;
}

export const SolutionListPanel: React.FC<SolutionListPanelProps> = ({
  testId,
  questionId,
  currentRecordingId,
  tenantId: propTenantId,
  isMobile = false,
  onMobileToggle
}) => {
  const [solutions, setSolutions] = useState<RecordingMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const userInfo = useUserStore(state => state.userInfo);
  const tenantId = useTenantStore(state => state.tenantId);

  useEffect(() => {
    if (questionId && testId) {
      loadSolutions();
    }
  }, [questionId, testId]);

  const loadSolutions = async () => {
    try {
      setLoading(true);
      // Use prop tenant ID first, then fall back to store
      const effectiveTenantId = propTenantId || tenantId;
      
      
      if (!effectiveTenantId) {
        setSolutions([]);
        return;
      }
      
      const recordings = await RecordingService.getSolutionsByQuestion(
        testId,
        questionId, 
        effectiveTenantId
      );
      setSolutions(recordings);
    } catch (error) {
      console.error('Failed to load solutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (recordingId: string) => {
    navigate(`/player/${recordingId}`);
  };

  const handleDelete = async (recordingId: string) => {
    if (!confirm('Bu çözümü silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await RecordingService.deleteRecording(recordingId);
      await loadSolutions(); // Reload the list
    } catch (error) {
      console.error('Failed to delete solution:', error);
      alert('Çözüm silinirken bir hata oluştu');
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp: any) => {
    try {
      let date: Date;
      if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
        date = timestamp.toDate();
      } else if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date();
      }
      
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  // Mobile: Show as bottom drawer
  if (isMobile) {
    return (
      <>
        {/* Mobile Toggle Button */}
        <button
          onClick={() => {
            setIsMobileDrawerOpen(!isMobileDrawerOpen);
            if (onMobileToggle) {
              onMobileToggle();
            }
          }}
          className="fixed bottom-20 right-4 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 z-40 sm:hidden"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          {solutions.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
              {solutions.length}
            </span>
          )}
        </button>

        {/* Mobile Drawer Overlay */}
        {isMobileDrawerOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 sm:hidden"
            onClick={() => setIsMobileDrawerOpen(false)}
          />
        )}

        {/* Mobile Drawer */}
        <div className={`fixed inset-x-0 bottom-0 bg-white transform transition-transform duration-300 ease-out sm:hidden z-50 ${
          isMobileDrawerOpen ? 'translate-y-0' : 'translate-y-full'
        }`} style={{ maxHeight: '70vh' }}>
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Çözümler</h3>
                <p className="text-sm text-gray-500">{solutions.length} çözüm bulundu</p>
              </div>
              <button
                onClick={() => setIsMobileDrawerOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : solutions.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Henüz çözüm eklenmemiş</p>
              ) : (
                solutions.map((solution) => {
                  const isOwner = isAuthenticated && userInfo?.id === solution.authorMetadata.id;
                  const isPublic = solution.solutionMetadata?.isPublic || false;
                  const allowDownload = solution.solutionMetadata?.allowDownload || false;
                  const isCurrent = solution.id === currentRecordingId;

                  return (
                    <div
                      key={solution.id}
                      className={`border rounded-lg p-3 ${
                        isCurrent ? 'border-blue-500 bg-blue-900/20' : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">
                            Çözüm {solution.solutionMetadata?.solutionNumber || 1}
                            {solution.solutionMetadata?.solutionName && (
                              <span className="text-gray-500 ml-1">
                                - {solution.solutionMetadata.solutionName}
                              </span>
                            )}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {solution.authorMetadata.name}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {isPublic ? (
                            <svg className="w-[14px] h-[14px] text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ) : (
                            <svg className="w-[14px] h-[14px] text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          )}
                          {allowDownload && (
                            <svg className="w-[14px] h-[14px] text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                            </svg>
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 mb-2">
                        <div>{formatDate(solution.createdAt)}</div>
                        {solution.duration && (
                          <div>Süre: {formatDuration(solution.duration)}</div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePlay(solution.id)}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                        >
                          <svg className="w-[14px] h-[14px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                          </svg>
                          {isCurrent ? 'Oynatılıyor' : 'Oynat'}
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => handleDelete(solution.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Sil"
                          >
                            <svg className="w-[14px] h-[14px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop: Show as side panel
  return (
    <>
      {/* Toggle Button - Always Visible */}
      <button
        onClick={() => {
          setIsCollapsed(!isCollapsed);
        }}
        className="hidden sm:flex fixed right-4 bottom-24 bg-gray-900/90 backdrop-blur border border-gray-700 shadow-md rounded-lg p-2 hover:bg-gray-800/50 transition-colors duration-200 z-50 items-center justify-center"
        aria-label={isCollapsed ? 'Çözüm panelini aç' : 'Çözüm panelini kapat'}
      >
        {isCollapsed ? (
          <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
        {solutions.length > 0 && isCollapsed && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
            {solutions.length}
          </span>
        )}
      </button>

      {/* Panel - Only show when not collapsed */}
      {!isCollapsed && (
        <>
          <div className="fixed right-0 top-[52px] bottom-[76px] w-80 bg-gray-900/95 backdrop-blur shadow-lg z-[60] border-l border-gray-800">
          <div className="h-full flex flex-col">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Çözümler</h3>
              <p className="text-sm text-gray-400">{solutions.length} çözüm bulundu</p>
            </div>
            <button
              onClick={() => setIsCollapsed(true)}
              className="p-2 hover:bg-gray-800/50 rounded-lg transition-colors"
              aria-label="Paneli kapat"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : solutions.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Henüz çözüm eklenmemiş</p>
            ) : (
              solutions.map((solution) => {
                const isOwner = isAuthenticated && userInfo?.id === solution.authorMetadata.id;
                const isPublic = solution.solutionMetadata?.isPublic || false;
                const allowDownload = solution.solutionMetadata?.allowDownload || false;
                const isCurrent = solution.id === currentRecordingId;

                return (
                  <div
                    key={solution.id}
                    className={`border rounded-lg p-3 ${
                      isCurrent ? 'border-blue-500 bg-blue-900/20' : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-white">
                          Çözüm {solution.solutionMetadata?.solutionNumber || 1}
                          {solution.solutionMetadata?.solutionName && (
                            <span className="text-gray-400 ml-1">
                              - {solution.solutionMetadata.solutionName}
                            </span>
                          )}
                        </h4>
                        <p className="text-xs text-gray-400">
                          {solution.authorMetadata.name}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {isPublic ? (
                          <svg className="w-[14px] h-[14px] text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-[14px] h-[14px] text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        )}
                        {allowDownload && (
                          <svg className="w-[14px] h-[14px] text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-gray-400 mb-2">
                      <div>{formatDate(solution.createdAt)}</div>
                      {solution.duration && (
                        <div>Süre: {formatDuration(solution.duration)}</div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePlay(solution.id)}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        <svg className="w-[14px] h-[14px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                        </svg>
                        {isCurrent ? 'Oynatılıyor' : 'Oynat'}
                      </button>
                      {isOwner && (
                        <button
                          onClick={() => handleDelete(solution.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Sil"
                        >
                          <svg className="w-[14px] h-[14px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        </div>
        </>
      )}
    </>
  );
};